import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { CheckSquare, X, MoreHorizontal, Edit } from "lucide-react";
import { useTranslation } from "react-i18next";

interface MultiSelectActionsProps {
  onDeleteSelected: () => void;
  onMergeSelected?: () => void;
  onMassUpdate?: () => void;
  selectedCount: number;
  isMultiSelectMode: boolean;
  onToggleMultiSelect: () => void;
  selectAll?: () => void;
}

export const MultiSelectActions = ({
  onDeleteSelected,
  onMergeSelected,
  onMassUpdate,
  selectedCount,
  isMultiSelectMode,
  onToggleMultiSelect,
  selectAll,
}: MultiSelectActionsProps) => {
  const { t } = useTranslation();

  if (!isMultiSelectMode) {
    return (
      <Button
        variant="outline"
        size="sm"
        onClick={onToggleMultiSelect}
        className="flex items-center gap-2"
      >
        <CheckSquare className="h-4 w-4" />
        {t("common.selectMultiple")}
      </Button>
    );
  }

  return (
    <div className="flex items-center gap-2">
      <Button
        variant="outline"
        size="sm"
        onClick={onToggleMultiSelect}
        className="flex items-center gap-2"
      >
        <X className="h-4 w-4" />
        {t("common.cancel")}
      </Button>
      {selectAll && (
        <Button
          variant="outline"
          size="sm"
          onClick={selectAll}
          className="flex items-center gap-2"
        >
          <CheckSquare className="h-4 w-4" />
          {t("common.selectAll")}
        </Button>
      )}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className="flex items-center gap-2"
            disabled={selectedCount === 0}
          >
            <MoreHorizontal className="h-4 w-4" />
            {t("common.actions")}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem
            onClick={onDeleteSelected}
            className="text-red-600"
            disabled={selectedCount === 0}
          >
            {t("common.deleteSelected", { count: selectedCount })}
          </DropdownMenuItem>
          {onMergeSelected && (
            <DropdownMenuItem
              onClick={onMergeSelected}
              disabled={selectedCount < 2}
            >
              {t("common.mergeSelected", { count: selectedCount }) || `Merge Selected (${selectedCount})`}
            </DropdownMenuItem>
          )}
          {onMassUpdate && (
            <DropdownMenuItem
              onClick={onMassUpdate}
              disabled={selectedCount === 0}
              className="flex items-center gap-2"
            >
              <Edit className="h-4 w-4" />
              Mass Update ({selectedCount})
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}; 